/**
 * Axis helpers for the clinical-statistics charts.
 *
 * `niceTicks` picks round tick values (1, 2, 2.5, 5 times a power of ten) that cover a
 * range, the way every plotting library does, so axes read "0, 20, 40" and not "0, 17.3".
 */

export interface Ticks {
  values: number[];
  /** The tick range, which is at least as wide as the data. */
  min: number;
  max: number;
  step: number;
}

const STEP_MULTIPLIERS = [1, 2, 2.5, 5, 10] as const;

/** Round the floating-point noise out of a tick value (0.30000000000000004 → 0.3). */
function clean(value: number, step: number): number {
  const digits = Math.max(0, 2 - Math.floor(Math.log10(step)));
  return Number(value.toFixed(Math.min(digits, 12)));
}

export function niceTicks(low: number, high: number, target = 6): Ticks {
  let min = low;
  let max = high;
  if (!(Number.isFinite(min) && Number.isFinite(max))) {
    return { values: [0, 1], min: 0, max: 1, step: 1 };
  }
  if (min === max) {
    const pad = min === 0 ? 1 : Math.abs(min) * 0.1;
    min -= pad;
    max += pad;
  }
  const rough = (max - min) / Math.max(1, target - 1);
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const step =
    (STEP_MULTIPLIERS.find((m) => m * magnitude >= rough) ?? 10) * magnitude;
  const first = Math.floor(min / step) * step;
  const last = Math.ceil(max / step) * step;
  const values: number[] = [];
  for (let i = 0; first + i * step <= last + step * 1e-9; i++) {
    values.push(clean(first + i * step, step));
  }
  return { values, min: values[0], max: values.at(-1) ?? last, step };
}

/** Linear map from a data range to a pixel range. */
export function linearScale(
  domain: readonly [number, number],
  range: readonly [number, number]
): (value: number) => number {
  const [d0, d1] = domain;
  const [r0, r1] = range;
  const span = d1 - d0 || 1;
  return (value) => r0 + ((value - d0) / span) * (r1 - r0);
}
